import { useForm } from 'react-hook-form'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-react'
import type { EmailPreferences } from '../types'

interface EmailPreferencesFormProps {
  preferences: EmailPreferences | null
  onSave: (preferences: Partial<EmailPreferences>) => Promise<void>
  isLoading?: boolean
}

interface FormData {
  marketingEmails: boolean
  artworkPublished: boolean
  artworkTrending: boolean
  salesNotifications: boolean
  weeklyDigest: boolean
  accountUpdates: boolean
}

const preferenceItems = [
  {
    key: 'marketingEmails' as const,
    label: 'Marketing Emails',
    description: 'Receive promotional emails and announcements',
  },
  {
    key: 'artworkPublished' as const,
    label: 'Artwork Published',
    description: 'Notifications when artworks are published to the store',
  },
  {
    key: 'artworkTrending' as const,
    label: 'Artwork Trending',
    description: 'Alerts when artworks gain significant attention',
  },
  {
    key: 'salesNotifications' as const,
    label: 'Sales Notifications',
    description: 'Alerts when sales are made',
  },
  {
    key: 'weeklyDigest' as const,
    label: 'Weekly Digest',
    description: 'Weekly summary of activity and stats',
  },
  {
    key: 'accountUpdates' as const,
    label: 'Account Updates',
    description: 'Important account and platform updates',
  },
]

export function EmailPreferencesForm({
  preferences,
  onSave,
  isLoading,
}: EmailPreferencesFormProps) {
  const { handleSubmit, watch, setValue } = useForm<FormData>({
    defaultValues: {
      marketingEmails: preferences?.marketingEmails ?? true,
      artworkPublished: preferences?.artworkPublished ?? true,
      artworkTrending: preferences?.artworkTrending ?? true,
      salesNotifications: preferences?.salesNotifications ?? true,
      weeklyDigest: preferences?.weeklyDigest ?? true,
      accountUpdates: preferences?.accountUpdates ?? true,
    },
  })

  const onSubmit = async (data: FormData) => {
    await onSave(data)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Email Preferences</CardTitle>
        <CardDescription>
          Manage what types of emails this artist receives
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-4">
            {preferenceItems.map((item) => (
              <div key={item.key} className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor={item.key}>{item.label}</Label>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </div>
                <Switch
                  id={item.key}
                  checked={watch(item.key)}
                  onCheckedChange={(checked) => setValue(item.key, checked)}
                />
              </div>
            ))}
          </div>

          {preferences?.unsubscribedAt && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm text-yellow-800">
                This user unsubscribed from all marketing emails on{' '}
                {new Date(preferences.unsubscribedAt).toLocaleDateString()}
              </p>
            </div>
          )}

          <Button type="submit" disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Preferences
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
